import { z } from "zod";
import { Label } from "components/label";
import { Controller, useForm } from "react-hook-form";
import { HttpError } from "@refinedev/core";
import { zodResolver } from "@hookform/resolvers/zod";
import { SwitchButton } from "components/switchButton";
import Button from "components/button";
import { Link } from "react-router-dom";
import { IHomepageCouponDetail, IHomepageCouponForm } from "interfaces";
import Input from "components/input";
import Textarea from "components/textarea";

export interface HomepageCouponFormProps {
  id?: string;
  defaultData?: IHomepageCouponDetail;
  onSubmit: (data: IHomepageCouponForm) => void;
}

const HomepageCouponSchema = z.object({
  is_active: z.boolean(),
  content_en: z.string().min(1, "This field is required"),
  content_vi: z.string().min(1, "This field is required"),
  coupon_code: z.string().min(1, "This field is required").max(20, "The maximum input is 20 characters"),
  title_en: z.string().min(1, "This field is required"),
  title_vi: z.string().min(1, "This field is required"),
  position:  z.number({
    required_error: "This field is required",
    invalid_type_error: "This field is required",
  })
    .min(1, {message:"Position must be equal or greater than 1"})
    .nonnegative({ message: "Position must be a positive number" }),
  is_sold_out: z.boolean(),
});

export const HomepageCouponForm = ({ id, defaultData, onSubmit }: HomepageCouponFormProps) => {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IHomepageCouponForm, HttpError, IHomepageCouponForm>({
    defaultValues: {
      content_en: defaultData?.content_en ?? "",
      content_vi: defaultData?.content_vi ?? "",
      coupon_code: defaultData?.coupon_code ?? "",
      is_active: defaultData?.is_active ?? true,
      is_sold_out: defaultData?.is_sold_out ?? false,
      position: defaultData?.position ?? null,
      title_en: defaultData?.title_en ?? "",
      title_vi: defaultData?.title_vi ?? "",
    },
    resolver: zodResolver(HomepageCouponSchema),
  });

  const _submit = (values: IHomepageCouponForm) => {
    const submitValues = { ...values };
    onSubmit(submitValues);
  };

  return (
    <div className="w-full ">
      <div>
        <div className="flex flex-col gap-y-2 sm:gap-y-4">
          <div className="w-full box-container">
            <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6">
              <div className="sm:col-span-3">
                <Label required id="is_active" name="Status" />
                <Controller
                  control={control}
                  name="is_active"
                  defaultValue={true}
                  render={({ field: { onChange, value = true, ref } }) => (
                    <SwitchButton checked={value} onChange={onChange} />
                  )}
                />
                {errors["is_active"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.is_active.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label id="is_sold_out" name="Soldout" />
                <Controller
                  control={control}
                  name="is_sold_out"
                  render={({ field: { onChange, value = true, ref } }) => (
                    <Input
                      type="checkbox"
                      label=""
                      id="has_face_recognition"
                      checked={value}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                        onChange(e.target.checked)
                      }    
                    />
                  )}
                />
                {errors["is_sold_out"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.is_sold_out.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label required id="coupon_code" name="Coupon code" />
                <Input
                  id="coupon_code"
                  type="text"
                  placeholder="Coupon code"
                  error={errors["coupon_code"]}
                  {...register("coupon_code")}
                />
                {errors["coupon_code"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.coupon_code.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label required id="position" name="Position" />
                <Input
                  id="position"
                  type="number"
                  min={1}
                  placeholder="Position"
                  error={errors["position"]}
                  {...register("position", {
                    valueAsNumber: true
                  })}
                />
                {errors["position"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.position.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label required id="title_vi" name="Title vie" />
                <Input
                  id="title_vi"
                  type="text"
                  placeholder="Title vie"
                  error={errors["title_vi"]}
                  {...register("title_vi")}
                />
                {errors["title_vi"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.title_vi.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label required id="title_en" name="Title eng" />
                <Input
                  id="title_en"
                  type="text"
                  placeholder="Title eng"
                  error={errors["title_en"]}
                  {...register("title_en")}
                />
                {errors["title_en"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.title_en.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label required id="content_vi" name="Content vie" />
                <Textarea
                  id="content_vi"
                  placeholder="Content vie"
                  error={errors["content_vi"]}
                  {...register("content_vi")}
                />
                {errors["content_vi"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.content_vi.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label required id="content_en" name="Content eng" />
                <Textarea
                  id="content_en"
                  placeholder="Content eng"
                  error={errors["content_en"]}
                  {...register("content_en")}
                />
                {errors["content_en"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.content_en.message}
                    </span>
                  </p>
                ) : null}
              </div>
            </div>
          </div>
          <div className="w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2] mt-auto">
            <div className="flex items-center justify-end gap-x-4">
              <Link to={`/mkt/homepage-coupon${id ? `/${id}` : ""}`}>
                <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                  Cancel
                </Button>
              </Link>
              <Button className="w-20" type="submit" onClick={handleSubmit(_submit)}>
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};